import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import "./style.scss";
import OliverText, { TextType } from "@builder/components/general/OliverText";
import FormInput from "@builder/components/general/FormInput";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { useOutsideClick } from "@builder/hooks/useOutsideClick";
import { removeBuildSettingKey } from "@builder/store/slices/BuilderSlice/builderSlice";
import {
    addBuildSettingsKey,
    BuildSettingsKey,
    deleteBuildSettingsKey,
    NewBuildSettingKey,
    updateBuildSettingsKey
} from "@builder/store/slices/buildSettingsKeys/buildSettingsKeySlice";
import { useTranslation } from "react-i18next";

interface CreateTextSettingFormProps {
    defaultValues?: Partial<BuildSettingsKey>;
    id?: string;
    closeEdit: () => void;
}

const CreateTextSettingForm = ({ defaultValues, closeEdit }: CreateTextSettingFormProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const build = useAppSelector(state => state.builder.build);
    const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
    const deleteConfirmRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const partialSetting: Partial<BuildSettingsKey> = {
        buildId: build.id,
        organizationId: build.companyId,
        companyId: build.companyId,
        dataType: "text"
    };

    useOutsideClick(formRef, () => closeEdit());
    useOutsideClick(deleteConfirmRef, () => setDeleteConfirm(false));

    const { register, handleSubmit, formState: { errors } } = useForm<Partial<BuildSettingsKey>>({
        defaultValues
    });

    const onSubmit = async (formData: Partial<BuildSettingsKey>) => {
        if (defaultValues?.key) {
            dispatch(
                updateBuildSettingsKey({
                    key: defaultValues.key,
                    update: { label: formData.label, description: formData.description, placeholder: formData.placeholder }
                })
            );
            closeEdit();
        } else {
            const fullSetting: Partial<BuildSettingsKey> = {
                ...partialSetting,
                label: formData.label,
                placeholder: formData.placeholder,
                description: formData.description
            };

            if (fullSetting.buildId && fullSetting.companyId && fullSetting.organizationId && fullSetting.label) {
                dispatch(addBuildSettingsKey(fullSetting as NewBuildSettingKey));
                closeEdit();
            }
        }
    };

    const handleDeleteBuildSetting = async () => {
        if (defaultValues?.key) {
            dispatch(deleteBuildSettingsKey(defaultValues.key));
            dispatch(removeBuildSettingKey(defaultValues.key));
            closeEdit();
        }
    };

    return (
        <form className="create-text-setting" ref={formRef} onSubmit={handleSubmit(onSubmit)}>
            <div className="create-ts-row">
                <div className="create-ts-col">
                    {!defaultValues && (
                        <div className="create-ts-group">
                            <OliverText type={TextType.SUBTITLE_3} text={t("checkoutFlow.creatingHeader")} />
                            <OliverText
                                type={TextType.BODY_3}
                                text={t("checkoutFlow.creatingSubtitle1") + ` ${build.name}` + t("checkoutFlow.creatingSubtitle2")}
                            />
                        </div>
                    )}
                    <div className="create-ts-input-wrapper">
                        <FormInput register={register}
                            name="label"
                            placeholder={t("checkoutFlow.dynTitlePlaceholder")}
                            label={t("checkoutFlow.dynTitle")}
                            validation={{ required: t("checkoutFlow.titleError") }}
                            errors={errors}
                        />
                    </div>
                    <div className="create-ts-input-wrapper">
                        <FormInput
                            register={register}
                            name="description"
                            placeholder={t("checkoutFlow.dynDescriptionPlaceholder")}
                            label={t("checkoutFlow.dynDescription")}
                            validation={{ required: t("checkoutFlow.descriptionError") }}
                            errors={errors}
                            textarea
                        />
                    </div>
                    <div className="create-ts-input-wrapper">
                        <FormInput
                            register={register}
                            name="placeholder"
                            placeholder={t("checkoutFlow.dynFallbackPlaceholder")}
                            label={t("checkoutFlow.dynFallback")}
                            validation={{ required: t("checkoutFlow.textFallbackError") }}
                            errors={errors}
                            textarea
                        />
                    </div>
                </div>
                {defaultValues &&
                    (deleteConfirm ? (
                        <div className="confirm-delete" ref={deleteConfirmRef}>
                            <OliverText type={TextType.BODY_4} color="red" text={t("checkoutFlow.delete")} />
                            <button onClick={() => setDeleteConfirm(false)}>
                                <img src="./x-icon-dark.svg" alt="" />
                            </button>
                            <button onClick={() => handleDeleteBuildSetting()}>
                                <img src="./check.svg" alt="" />
                            </button>
                        </div>
                    ) : (
                        <button className="create-ts-x" type="button" onClick={() => setDeleteConfirm(true)}>
                            <img src="./trash.svg" alt="" />
                        </button>
                    ))}
            </div>
            <div className="create-ts-row">
                <button className="cancel ts-edit-button" type="button" onClick={() => closeEdit()}>
                    <OliverText type={TextType.BODY_2}>{t("checkoutFlow.cancel")}</OliverText>
                </button>
                <button className="submit ts-edit-button">
                    <OliverText type={TextType.BODY_2}>{t("checkoutFlow.add")}</OliverText>
                </button>
            </div>
        </form>
    );
};

export default CreateTextSettingForm;
